import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  useScrollTrigger,
  Container,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DesktopUserMenu from './DesktopUserMenu';
import { NAV_ITEMS, PLATFORM_URL, BRAND_NAME } from '../constants/site';
import { brandColors } from '../theme';

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 24 });
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userRole, loading, loginWithGoogle, logout } = useAuth();
  const isHome = location.pathname === '/';
  const scrolled = trigger;

  const handleDrawerToggle = () => setMobileOpen((open) => !open);
  const closeMobileMenu = () => setMobileOpen(false);

  const scrollToSection = (id) => {
    closeMobileMenu();
    if (!isHome) {
      navigate('/');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 150);
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleGoToPlatform = () => {
    closeMobileMenu();
    window.location.href = PLATFORM_URL;
  };

  const handleGoToAdminPanel = () => {
    closeMobileMenu();
    navigate('/admin');
  };

  const handleLogout = async () => {
    closeMobileMenu();
    await logout();
  };

  const drawer = (
    <Box sx={{ width: 280, py: 2 }}>
      <Typography variant="h6" sx={{ px: 2, mb: 2, fontWeight: 800, color: brandColors.navy }}>
        {BRAND_NAME}
      </Typography>
      <List>
        {NAV_ITEMS.map((item) => (
          <ListItem key={item.name} disablePadding>
            <ListItemButton onClick={() => scrollToSection(item.id)}>
              <ListItemText primary={item.name} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding>
          <ListItemButton onClick={() => scrollToSection('contact')}>
            <ListItemText
              primary="Consulta gratis"
              primaryTypographyProps={{ fontWeight: 700, color: brandColors.gold }}
            />
          </ListItemButton>
        </ListItem>
        {currentUser ? (
          <>
            {userRole === 'admin' && (
              <ListItem disablePadding>
                <ListItemButton onClick={handleGoToAdminPanel}>
                  <ListItemIcon><AdminPanelSettingsIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Administración" />
                </ListItemButton>
              </ListItem>
            )}
            {(userRole === 'client' || userRole === 'admin') && (
              <ListItem disablePadding>
                <ListItemButton onClick={handleGoToPlatform}>
                  <ListItemIcon><DashboardIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Plataforma" />
                </ListItemButton>
              </ListItem>
            )}
            <ListItem disablePadding>
              <ListItemButton onClick={handleLogout}>
                <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Cerrar sesión" />
              </ListItemButton>
            </ListItem>
          </>
        ) : (
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                closeMobileMenu();
                loginWithGoogle();
              }}
            >
              <ListItemIcon><LoginIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Acceso clientes" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        color="transparent"
        sx={{
          color: '#fff',
          bgcolor: isHome
            ? scrolled
              ? 'rgba(11, 20, 38, 0.95)'
              : 'rgba(11, 20, 38, 0.72)'
            : 'rgba(11, 20, 38, 0.95)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          transition: 'background-color 0.3s ease',
          '& .MuiButton-root': { color: '#fff' },
          '& .MuiIconButton-root': { color: '#fff' },
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Toolbar
            disableGutters
            sx={{
              py: 0.5,
              minHeight: { xs: 56, sm: 64 },
              gap: 1,
              overflow: 'hidden',
              width: '100%',
            }}
          >
            <Typography
              variant="h6"
              component={Link}
              to="/"
              sx={{
                flexGrow: 1,
                minWidth: 0,
                textDecoration: 'none',
                color: 'inherit',
                fontWeight: 800,
                letterSpacing: '-0.02em',
                fontSize: { xs: '1.15rem', sm: '1.25rem' },
              }}
            >
              {BRAND_NAME}
            </Typography>

            <IconButton
              color="inherit"
              aria-label="menu"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>

            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
              {NAV_ITEMS.map((item) => (
                <Button
                  key={item.name}
                  color="inherit"
                  onClick={() => scrollToSection(item.id)}
                  sx={{
                    opacity: 0.9,
                    '&:hover': { opacity: 1, color: brandColors.goldLight },
                  }}
                >
                  {item.name}
                </Button>
              ))}
              <Button
                variant="contained"
                color="secondary"
                size="small"
                onClick={() => scrollToSection('contact')}
                sx={{ ml: 1, fontWeight: 700 }}
              >
                Consulta gratis
              </Button>
              {!loading && <DesktopUserMenu />}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={closeMobileMenu}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box' },
        }}
      >
        {drawer}
      </Drawer>
      <Toolbar sx={{ visibility: 'hidden' }} />
    </>
  );
};

export default Header;
